/**
 * Comparison Table Section
 * Feature comparison vs OpenRouter and LiteLLM
 */

import { motion } from 'framer-motion';
import { Check, X, Minus } from 'lucide-react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type FeatureStatus = 'full' | 'partial' | 'none';

interface Feature {
  name: string;
  description: string;
  pathfinder: FeatureStatus;
  openrouter: FeatureStatus;
  litellm: FeatureStatus;
}

const features: Feature[] = [
  {
    name: 'Semantic Caching',
    description: 'Reuse responses based on meaning, not just exact matches',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'full',
  },
  {
    name: 'Prompt Compression',
    description: 'Condense long context before inference',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'Prompt Caching Controls',
    description: 'Leverage provider-side caching when available',
    pathfinder: 'full',
    openrouter: 'partial',
    litellm: 'partial',
  },
  {
    name: 'Zero Prompt Storage',
    description: 'Never stores raw prompts, only SHA-256 hashes',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'ML-Powered Routing',
    description: 'Learns from feedback to improve model selection',
    pathfinder: 'full',
    openrouter: 'partial',
    litellm: 'none',
  },
  {
    name: 'Transparent Pricing',
    description: 'Provider rates plus a published platform fee',
    pathfinder: 'full',
    openrouter: 'full',
    litellm: 'full',
  },
  {
    name: 'Training Telemetry',
    description: 'Real-time training progress via Redis streams',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'Shadow Mode',
    description: 'Test routing decisions without affecting production',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'Multi-Tenant Isolation',
    description: 'Row-level security with PostgreSQL RLS',
    pathfinder: 'full',
    openrouter: 'partial',
    litellm: 'partial',
  },
  {
    name: 'Advanced Analytics',
    description: 'Cost breakdown, latency p95/p99, model performance',
    pathfinder: 'full',
    openrouter: 'partial',
    litellm: 'partial',
  },
  {
    name: 'ROI Calculator',
    description: 'Built-in tool to forecast savings',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'Provider Uptime Dashboard',
    description: 'Real-time health monitoring for all providers',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'none',
  },
  {
    name: 'Custom Routing Policies',
    description: 'Define your own routing logic in Python or JSON',
    pathfinder: 'full',
    openrouter: 'partial',
    litellm: 'partial',
  },
  {
    name: 'OpenAI SDK Compatible',
    description: 'Drop-in replacement, no code changes',
    pathfinder: 'full',
    openrouter: 'full',
    litellm: 'full',
  },
  {
    name: 'Open Source',
    description: 'Self-hostable with full source code access',
    pathfinder: 'full',
    openrouter: 'none',
    litellm: 'full',
  },
];

function StatusIcon({ status }: { status: FeatureStatus }) {
  switch (status) {
    case 'full':
      return <Check className="w-5 h-5 text-green-500 mx-auto" />;
    case 'partial':
      return <Minus className="w-5 h-5 text-yellow-500 mx-auto" />;
    case 'none':
      return <X className="w-5 h-5 text-red-500 mx-auto" />;
  }
}

function StatusLabel({ status }: { status: FeatureStatus }) {
  switch (status) {
    case 'full':
      return <span className="text-green-500 text-xs">Full</span>;
    case 'partial':
      return <span className="text-yellow-500 text-xs">Partial</span>;
    case 'none':
      return <span className="text-red-500 text-xs">None</span>;
  }
}

export function ComparisonSection() {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="backdrop-blur-md bg-background/60 border border-border/50 rounded-2xl p-8 md:p-12 shadow-xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Naivana?</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Feature-by-feature comparison with OpenRouter and LiteLLM
            </p>
          </div>
        </motion.div>

        {/* Legend */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-500" />
            <span className="text-muted-foreground">Full Support</span>
          </div>
          <div className="flex items-center space-x-2">
            <Minus className="w-4 h-4 text-yellow-500" />
            <span className="text-muted-foreground">Partial Support</span>
          </div>
          <div className="flex items-center space-x-2">
            <X className="w-4 h-4 text-red-500" />
            <span className="text-muted-foreground">Not Available</span>
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="overflow-x-auto"
        >
          <motion.div
            variants={itemVariants}
            className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl overflow-hidden shadow-xl"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/4 font-semibold">Feature</TableHead>
                  <TableHead className="text-center bg-primary/10 font-semibold">
                    Naivana
                    <div className="text-xs font-normal text-muted-foreground mt-1">
                      (You are here)
                    </div>
                  </TableHead>
                  <TableHead className="text-center font-semibold">OpenRouter</TableHead>
                  <TableHead className="text-center font-semibold">LiteLLM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((feature, index) => (
                  <TableRow
                    key={index}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <TableCell className="font-medium">
                      <div>
                        <div className="font-semibold mb-1">{feature.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {feature.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="bg-primary/5 text-center">
                      <div className="flex flex-col items-center space-y-1">
                        <StatusIcon status={feature.pathfinder} />
                        <StatusLabel status={feature.pathfinder} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center space-y-1">
                        <StatusIcon status={feature.openrouter} />
                        <StatusLabel status={feature.openrouter} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center space-y-1">
                        <StatusIcon status={feature.litellm} />
                        <StatusLabel status={feature.litellm} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>
        </motion.div>

        {/* Bottom Note */}
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-muted-foreground">
            Data last updated: November 2025. Comparison based on publicly available information.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
